<template>
  <b-button
    :variant="variant"
    :disabled="disabled"
    :title="lockedReason"
    size="sm"
    class="resume-request-btn"
    @click.stop="onClick"
  >
    {{ label }}
  </b-button>
</template>

<script>
import { mapGetters } from 'vuex'

// State-aware CTA used on both CandidateCard and ViewCandidate. `status` is
// the candidate's `resume_request_status`: null | pending | accepted | declined.
// Clicking opens ResumeRequestModal (parent's responsibility) for
// confirmation — this component never calls the API directly, matching the
// existing assign-token confirm-then-dispatch pattern (AssignTokenModal +
// CompanyTradieList.onAssignToken).
export default {
  name: 'ResumeRequestButton',

  props: {
    tradieId: {
      type: [Number, String],
      required: true,
    },
    status: {
      type: String,
      default: null,
    },
    // A declined request the company can still re-send (server is under its
    // retry cap). Lets the CTA offer "Request Resume Again" instead of a dead
    // "Resume Declined".
    retryable: {
      type: Boolean,
      default: false,
    },
  },

  computed: {
    ...mapGetters('auth', ['hasPermission']),

    canRequestResume() {
      return this.hasPermission('candidates.request_resume')
    },

    label() {
      if (this.status === 'pending') return 'Resume Pending'
      if (this.status === 'accepted') return 'Resume Accepted'
      // A decline can be a mis-tap — offer another go while the server still
      // allows it (retryable); once the retry cap is hit it's terminal.
      if (this.status === 'declined') {
        return this.retryable ? 'Request Resume Again' : 'Resume Declined'
      }
      return 'Request Resume'
    },

    variant() {
      if (this.status === 'accepted') return 'success'
      if (this.status === 'pending') return 'outline-secondary'
      if (this.status === 'declined') {
        return this.retryable ? 'outline-primary' : 'outline-danger'
      }
      return 'primary'
    },

    // Why the button is dead, so a red "Resume Declined" is not a mystery.
    lockedReason() {
      if (this.status === 'declined' && !this.retryable) {
        return 'This candidate has declined your resume requests several times recently, so you cannot ask again for now.'
      }
      return null
    },

    disabled() {
      if (!this.canRequestResume) return true
      if (this.status === 'pending' || this.status === 'accepted') return true
      if (this.status === 'declined') return !this.retryable
      return false
    },
  },

  methods: {
    onClick() {
      if (this.disabled) return
      this.$emit('request', this.tradieId)
    },
  },
}
</script>

<style lang="scss" scoped>
.resume-request-btn {
  // Was nowrap, but this button sits in a 2-column grid on the candidate
  // profile page (ViewCandidate.vue) that squeezes it to roughly half the
  // card's width — too narrow for the longer labels ("Resume Accepted") to
  // fit on one line, so nowrap was clipping the text instead of wrapping it.
  // Letting it wrap keeps it on one line wherever there's room (e.g.
  // CandidateCard.vue) and wraps cleanly to two lines where there isn't.
  white-space: normal;
}
</style>
